import { Typography } from "@suraasa/placebo-ui"
import {
  AssignmentList,
  SUBMISSION_STATUS,
  SubmissionResultEnum,
} from "api/resources/assignments/types"
import { getRestriction } from "features/AccessManagement/CheckRestrictions"
import { getActionConfig } from "features/Assignments/utils"
import routes from "routes"
import { formatDate } from "utils/helpers"

export const hasNotStartedAnyAssignmentYet = (
  assignments: AssignmentList[]
) => {
  return assignments.every(assignment => {
    const config = getActionConfig(assignment)
    return config.canAttempt && !config.isDraft
  })
}

export const getDueDate = (item: AssignmentList) => {
  if (!item.dueDate) return null

  const date = (
    <Typography variant="smallBody" className="text-onSurface-500">
      Due by {formatDate(item.dueDate, "d MMM y, h:mm a")}
    </Typography>
  )

  // Show due date only in case of due, overdue, saved_for_later, rejected and failed
  if (item.result === SubmissionResultEnum.FAILED) return date
  switch (item.submissionStatus) {
    case null:
    case undefined:
    case SUBMISSION_STATUS.rejected:
    case SUBMISSION_STATUS.plagiarised:
    case SUBMISSION_STATUS.savedForLater:
      return date
  }

  return null
}

export const getAssignmentUrl = (
  item: AssignmentList,
  extras: {
    slug: string
    canAttempt: boolean
    isDraft: boolean | undefined
  }
) => {
  if (extras.canAttempt) {
    const path = routes.assignment
      .replace(":slug", extras.slug)
      .replace(":learningItemType", "course")
      .replace(":assignmentId", item.id.toString())

    const url = new URL(path, window.location.origin)
    if (extras.isDraft && item.submissionUuid)
      url.searchParams.set("submissionId", item.submissionUuid)

    return url.pathname + url.search
  }

  if (item.submissionUuid)
    return routes.submission
      .replace(":slug", extras.slug)
      .replace(":learningItemType", "course")
      .replace(":assignmentId", item.id.toString())
      .replace(":submissionId", item.submissionUuid)

  return routes.assignment
    .replace(":slug", extras.slug)
    .replace(":learningItemType", "course")
    .replace(":assignmentId", item.id.toString())
}

/**
 * Derives the interaction state for an assignment row. The whole row is the
 * click target, so `to` is only produced when the row is actually navigable.
 * Pass `disableLink` when the row should open a sheet instead of navigating.
 */
export const getAssignmentRowState = (
  item: AssignmentList,
  config: { canAttempt: boolean; isDraft: boolean | undefined },
  { slug, disableLink }: { slug: string; disableLink?: boolean }
) => {
  const isLocked = item.dueDate === null
  const isRestricted = getRestriction({ accessStatus: item.accessStatus })
  const canInteract = !isLocked && !isRestricted

  return {
    isLocked,
    isRestricted,
    canInteract,
    to:
      canInteract && !disableLink
        ? getAssignmentUrl(item, {
            slug,
            canAttempt: config.canAttempt,
            isDraft: config.isDraft,
          })
        : undefined,
  }
}

/**
 * Visual stand-in for the old "View" button. The row itself is the anchor now,
 * so this must not render a real <button> — interactive content cannot be
 * nested inside <a>. Mirrors `<Button variant="text" />` styling.
 */
export const ViewAction = () => (
  <span className="inline-flex items-center justify-center rounded-[10px] px-2 py-1 text-button text-primary">
    View
  </span>
)
